import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';

interface SignUpFormProps {
  onSuccess?: () => void;
}

const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
const formId = import.meta.env.VITE_HUBSPOT_GENERAL_FORM_ID;

export default function SignUpForm({ onSuccess }: SignUpFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formElement = e.target as HTMLFormElement;
    const formData = new FormData(formElement);
    const hutk = document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, "$1");

    try {
      // Track form submission start
      facebookPixelEvents.lead({
        content_name: 'General Signup',
        content_category: 'general',
        content_type: 'form_submission'
      });

      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: [
            {
              objectTypeId: "0-1",
              name: "firstname",
              value: formData.get('name')?.toString().split(' ')[0]
            },
            {
              objectTypeId: "0-1",
              name: "lastname",
              value: formData.get('name')?.toString().split(' ').slice(1).join(' ') || "."
            },
            {
              objectTypeId: "0-1",
              name: "email",
              value: formData.get('email')
            },
            {
              objectTypeId: "0-1",
              name: "phone",
              value: formData.get('phone')
            },
            {
              objectTypeId: "0-1",
              name: "additional_information",
              value: formData.get('additional_information')
            }
          ],
          context: {
            pageUri: window.location.href,
            pageName: document.title,
            ...(hutk && { hutk })
          },
          legalConsentOptions: {
            consent: {
              consentToProcess: true,
              text: "EmploSkills is committed to protecting and respecting your privacy, and we'll only use your personal information to administer your account and to provide the products and services you requested from us.",
              communications: [
                {
                  value: Boolean(formData.get('marketingConsent')),
                  subscriptionTypeId: 999,
                  text: "I agree to receive communications from EmploSkills"
                }
              ]
            }
          }
        })
      });

      if (!response.ok) throw new Error('Submission failed');
      
      setSubmitStatus('success');
      if (onSuccess) {
        setTimeout(onSuccess, 1500);
      }

      // Track form submission completion
      facebookPixelEvents.completeRegistration({
        content_name: 'General Registration Complete',
        content_category: 'general',
        content_type: 'registration_success'
      });

    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {submitStatus === 'success' && (
        <div className="bg-green-50 p-4 rounded-md flex items-center text-green-700">
          <CheckCircle className="w-5 h-5 mr-2" />
          Successfully signed up!
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 p-4 rounded-md flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          Something went wrong. Please try again.
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="John Doe"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          Phone Number
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="+1 (555) 123-4567"
        />
      </div>

      <div>
        <label htmlFor="additional_information" className="block text-sm font-medium text-gray-700">
          Additional Information
        </label>
        <textarea
          id="additional_information"
          name="additional_information"
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="Please share any additional details about your group or specific requirements..."
        />
      </div>

      <div className="mt-6">
        <PrivacyConsent 
          onConsentChange={(field, value) => console.log(field, value)}
          required={true}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white transition-all duration-200 ${
          isSubmitting 
            ? 'bg-blue-400 cursor-not-allowed' 
            : 'bg-primary hover:bg-blue-700'
        }`}
      >
        <Send className="w-5 h-5 mr-2" />
        {isSubmitting ? 'Signing Up...' : 'Sign Up Now'}
      </button>
    </form>
  );
}